import { loadAndCacheImage } from '../../../loaders/imageLoader.js';
import { isAcquisitionPlaneNormal } from '../../../utilities/isAcquisitionPlaneNormal.js';
import { VOILUTFunctionType } from '../../../enums/index.js';
import { ActorRenderMode } from '../../../types/index.js';
import { coreLog } from '../../../utilities/logger.js';
import { applyPlanarICameraToActor } from './planarRenderCamera.js';
import { getPlanarRenderPathActiveSourceICamera } from './planarRenderPathProjection.js';
import { VtkImageMapperRenderPath } from './VtkImageMapperRenderPath.js';
const log = coreLog.getLogger('RenderingEngine', 'planarAcquiredFrames');
export const acquiredFrameActors = new WeakSet();
function nearestSliceIndex(imageData, point) {
    const index = imageData.worldToIndex([...point]);
    index[2] = Math.round(index[2]);
    return index;
}
function framePlaneOffset(imageVolume, focalPoint, viewPlaneNormal) {
    const { imageData } = imageVolume;
    const framePoint = imageData.indexToWorld(nearestSliceIndex(imageData, focalPoint));
    const distance = (focalPoint[0] - framePoint[0]) * viewPlaneNormal[0] +
        (focalPoint[1] - framePoint[1]) * viewPlaneNormal[1] +
        (focalPoint[2] - framePoint[2]) * viewPlaneNormal[2];
    return [
        distance * viewPlaneNormal[0],
        distance * viewPlaneNormal[1],
        distance * viewPlaneNormal[2],
    ];
}
export function acquiredFrameIndexOfVolumePoint(imageVolume, imageIds, point) {
    const imageData = imageVolume.imageData;
    if (!imageData) {
        return;
    }
    const k = nearestSliceIndex(imageData, point)[2];
    if (!(k >= 0 && k < imageData.getDimensions()[2])) {
        return;
    }
    const imageId = imageVolume.imageIds?.[k];
    if (!imageId || imageIds[k] === imageId) {
        return k < imageIds.length ? k : undefined;
    }
    const imageIdIndex = imageIds.indexOf(imageId);
    return imageIdIndex >= 0 ? imageIdIndex : undefined;
}
export function withAcquiredFrames(ctx, data, volume) {
    const volumeRendering = volume.rendering;
    const { imageVolume, imageIds } = volumeRendering;
    let frame;
    let mounting = false;
    let removed = false;
    let wantedIndex;
    const frameRendering = () => frame?.rendering;
    const isSource = () => ctx.viewport.isCurrentDataId(data.id);
    const frameCtx = {
        ...ctx,
        viewport: { ...ctx.viewport, isCurrentDataId: () => false },
        display: {
            ...ctx.display,
            activateRenderMode: () => undefined,
            requestRender: () => {
                updateVisibility();
                ctx.display.requestRender();
            },
        },
    };
    const framePresentation = () => ({
        ...volumeRendering.dataPresentation,
        voiRange: volumeRendering.dataPresentation?.voiRange ??
            volumeRendering.defaultVOIRange,
        voiLUTFunction: VOILUTFunctionType.LINEAR,
    });
    function updateVisibility() {
        const rendering = frameRendering();
        const showsFrame = wantedIndex !== undefined &&
            rendering?.currentImageIdIndex === wantedIndex;
        const visible = volumeRendering.dataPresentation?.visible !== false;
        volumeRendering.actor.setVisibility(visible && !showsFrame);
        if (!volumeRendering.isSegmentationOverlay) {
            volumeRendering.fidelityStale ||=
                !showsFrame && volumeRendering.showsAcquiredFrame;
            volumeRendering.excludeFromFidelity = showsFrame;
        }
        volumeRendering.showsAcquiredFrame = showsFrame;
        if (!rendering) {
            return;
        }
        const camera = getPlanarRenderPathActiveSourceICamera(ctx);
        rendering.actor.setVisibility(visible && showsFrame);
        rendering.excludeFromFidelity = !showsFrame;
        applyPlanarICameraToActor({
            actor: rendering.actor,
            activeSourceICamera: camera,
        });
        if (showsFrame && camera) {
            rendering.actor.setPosition(...framePlaneOffset(imageVolume, camera.focalPoint, camera.viewPlaneNormal));
        }
    }
    async function mountFrame(imageIdIndex) {
        mounting = true;
        try {
            const image = await loadAndCacheImage(imageIds[imageIdIndex]);
            const attachment = await new VtkImageMapperRenderPath().addData(frameCtx, {
                ...data,
                image,
                imageData: undefined,
                initialImageIdIndex: imageIdIndex,
            }, { renderMode: ActorRenderMode.VTK_IMAGE, role: 'overlay' });
            if (removed) {
                attachment.removeData();
                return;
            }
            frame = attachment;
            acquiredFrameActors.add(frameRendering().actor);
            frame.updateDataPresentation(framePresentation());
        }
        catch (error) {
            log.warn('The acquired frame did not load', error);
            return;
        }
        finally {
            mounting = false;
        }
        sync();
        ctx.display.requestRender();
    }
    function sync() {
        if (removed) {
            return;
        }
        const camera = getPlanarRenderPathActiveSourceICamera(ctx);
        wantedIndex =
            camera &&
                isSource() &&
                ctx.viewport.canDrawAcquiredFrames?.(data.id) &&
                isAcquisitionPlaneNormal(imageVolume, camera.viewPlaneNormal)
                ? acquiredFrameIndexOfVolumePoint(imageVolume, imageIds, camera.focalPoint)
                : undefined;
        if (wantedIndex !== undefined) {
            if (frame) {
                frame.applyViewState({
                    ...ctx.viewport.getViewState(),
                    slice: { kind: 'stackIndex', imageIdIndex: wantedIndex },
                });
            }
            else if (!mounting) {
                void mountFrame(wantedIndex);
            }
        }
        updateVisibility();
    }
    const frameActorEntry = () => {
        const rendering = frameRendering();
        return (rendering && {
            uid: rendering.actorEntryUID,
            actor: rendering.actor,
            actorMapper: {
                actor: rendering.actor,
                mapper: rendering.mapper,
                renderMode: ActorRenderMode.VTK_IMAGE,
            },
        });
    };
    return {
        ...volume,
        updateDataPresentation: (props) => {
            volume.updateDataPresentation(props);
            frame?.updateDataPresentation(framePresentation());
            sync();
        },
        applyViewState: (viewState) => {
            volume.applyViewState(viewState);
            sync();
        },
        resize: () => {
            volume.resize?.();
            sync();
        },
        getActorEntries: (loadedData) => [volume.getActorEntry?.(loadedData), frameActorEntry()].filter(Boolean),
        getRenderings: () => frame ? [volume.rendering, frame.rendering] : [volume.rendering],
        removeData: () => {
            removed = true;
            frame?.removeData();
            volume.removeData();
        },
    };
}
